import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Grid,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  CircularProgress,
} from '@mui/material';

import { APP_CONFIG } from '../../../../config/app.config';
import { PurchaseOrderItemStatus, PurchaseOrderItemStatusColors } from '@domain/purchasing';
import { JSEditableInput, Label } from '@shared/ui/common';
import { usePurchasing } from '../../hooks/usePurchasing';
import { ChevronLeft, Close, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@shared/hooks/useToast';
import ShipmentPreview from '@features/purchasing/components/preview/ShipmentPreview';
import { usePurchaseOrderItems } from '../../hooks';
import { PurchaseOrderNotes } from '../notes/PurchaseOrderNotes';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { PurchaseOrderDraft } from './PurchaseOrderDraft';
import { EditShipmentDetails } from '@features/shipments/components/edit/EditShipmentDetails';
import { format } from 'date-fns';
import { groupByShipment, getUngroupedItems } from '../../utils/groupItems';
import { useMemo } from 'react';
import { JSDatePicker } from '@shared/ui/common/JSDatePicker';
import { GroupingOption } from '../../types/types';
import { PurchaseOrderShipment } from '../shipment/PurchaseOrderShipment';
import { Add } from '@mui/icons-material';
import { Product } from '@domain/product/models/Product';
import ProductDetailsDrawer from '@features/products/components/detail/ProductDetailsDrawer';
import { usePurchaseOrderPdfActions } from '../../hooks/usePurchaseOrderPdfActions';
import { Icon, IconSize } from '@shared/icons';
import { DeleteDialog } from '@shared/ui/dialog/DeleteDialog';


interface PurchaseOrderDetailsProps {
  purchaseOrderId?: string;
}

const PurchaseOrderDetails: React.FC<PurchaseOrderDetailsProps> = ({
  purchaseOrderId
}) => {
  const { closeDrawer, openDrawer, onBack, onClose } = useGenericDrawer();
  const navigate = useNavigate();
  const toast = useToast();

  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const { purchaseOrder, deletePurchaseOrder, updatePurchaseOrder, isLoadingPurchaseOrder, isDeleting } = usePurchasing(purchaseOrderId);
  const { purchaseOrderItems, isLoadingItems } = usePurchaseOrderItems({ filters: { purchaseOrderId } });

  const { updatePurchaseOrderItemStatus, updatePurchaseOrderItem } = usePurchaseOrderItems();

  const { isPdfBusy, downloadOrderPdf } = usePurchaseOrderPdfActions({
    purchaseOrderId,
    poNumber: purchaseOrder?.poNumber,
  });

  // Group items locally by shipment
  const { groupedItems, ungroupedItems } = useMemo(() => {
    if (!purchaseOrderItems) return { groupedItems: [], ungroupedItems: [] };

    const grouped = groupByShipment(purchaseOrderItems);
    const ungrouped = getUngroupedItems(purchaseOrderItems, GroupingOption.SHIPMENT);

    return { groupedItems: grouped, ungroupedItems: ungrouped };
  }, [purchaseOrderItems]);

  // State for delete confirmations
  const [deleteOrderDialogOpen, setDeleteOrderDialogOpen] = useState(false);

  // State for more menus
  const [orderMoreMenuAnchorEl, setOrderMoreMenuAnchorEl] = useState<null | HTMLElement>(null);


  const handleQuantityChange = (itemId: string, quantity?: number) => {
    if (!purchaseOrderId) return;

    if (quantity) {
      updatePurchaseOrderItem({ itemId, quantity });
    }
  };

  const handleStatusChange = (itemId: string, status: PurchaseOrderItemStatus) => {
    if (!purchaseOrderId) return;
    updatePurchaseOrderItemStatus({ itemId, status });
  };

  const handleOrderDateChange = async (newOrderDate: Date) => {
    if (!purchaseOrderId) return;

    await updatePurchaseOrder({ id: purchaseOrderId, purchaseOrder: { orderDate: new Date(newOrderDate) } });
  };

  const handleOrderMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOrderMoreMenuAnchorEl(event.currentTarget);
  };

  const handleOrderMoreMenuClose = () => {
    setOrderMoreMenuAnchorEl(null);
  };


  const handleDeletePurchaseOrder = async () => {
    if (!purchaseOrderId) return;

    try {
      await deletePurchaseOrder(purchaseOrderId);
      toast.success('Purchase order deleted successfully');
      closeDrawer();
      navigate('/purchasing');
    } catch (error) {
      console.error('Failed to delete purchase order:', error);
      toast.error('Failed to delete purchase order');
    }
  };

  const handleAddShipment = () => {
    if (!purchaseOrder || !purchaseOrderItems) return;

    openBottomSheet({
      component: (
        <PurchaseOrderShipment
          selectedShipment={undefined}
          handleClose={() => closeBottomSheet()}
          handleFinish={(shipment) => {
            console.log('Shipment created:', shipment);
            closeBottomSheet();
          }}
          products={purchaseOrder.items.map(item => item.product)}
          purchaseOrder={purchaseOrder}
          purchaseOrderItems={purchaseOrderItems}
        />
      ),
      title: 'Add Shipment',
      closeOnBackdropClick: true,
    });
  };


  const openPurchaseOrderEdit = () => {
    if (!purchaseOrderId) return;
    setOrderMoreMenuAnchorEl(null);
    openBottomSheet({
      component: <PurchaseOrderDraft purchaseOrderId={purchaseOrderId} />,
      title: 'Purchase Order Edit',
      closeOnBackdropClick: true,
    });
  }

  const openProductDetailsDrawer = (product: Product) => {
    if (!product.id) return;

    openDrawer(
      `Product ${product.title || ''}`,
      <ProductDetailsDrawer productId={product.id} />,
      () => openDrawer('Purchase Order Details', <PurchaseOrderDetails purchaseOrderId={purchaseOrderId} />)
    );
  }

  const openEditShipment = (shipment: any) => {
    if (!purchaseOrderItems) return;

    openBottomSheet({
      component: (
        <EditShipmentDetails
          shipmentId={shipment.id!}
          onFinish={() => { closeBottomSheet() }}
          onClose={() => closeBottomSheet()}
        />
      ),
      title: 'Edit Shipment',
      closeOnBackdropClick: true,
    });
  }

  return (
    <Stack sx={{ flexGrow: 1, p: 3, gap: 3 }}>

      {(isLoadingPurchaseOrder || isLoadingItems) && (
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="text" height={40} width="70%" />
          <Skeleton variant="text" height={30} width="50%" />
          <Skeleton variant="rectangular" height={200} />
        </Box>
      )}

      {!isLoadingPurchaseOrder && !purchaseOrder && (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Purchase order information not available
          </Typography>
        </Box>
      )}


      {/* Purchase Order Details Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" >
        <Stack direction="row" alignItems="center" spacing={0}>
          {onBack && (
            <IconButton onClick={onBack} edge="start">
              <ChevronLeft />
            </IconButton>
          )}
          <Typography variant="h6">Purchase Order Details</Typography>
        </Stack>

        <Stack direction="row" spacing={1}>


          <IconButton
            onClick={handleOrderMoreMenuOpen}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>

          {onClose && (
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {purchaseOrderItems && (
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" justifyContent="space-between" borderBottom={1} borderColor="divider" pb={2} >

          <Typography variant="subtitle1" color="text.primary">
            {purchaseOrder?.supplier ? purchaseOrder.supplier.name : 'No supplier'}
          </Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" justifyContent="flex-end" >
            <Button
              variant="text"
              color="secondary"
              size="small"
              startIcon={isPdfBusy ? <CircularProgress size={16} /> : <Icon icon="Download01" size={IconSize.MEDIUM} />}
              disabled={isPdfBusy}
              onClick={() => void downloadOrderPdf()}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>
      )
      }

      {/* PO Number */}

      {/* Order Details */}
      <Stack gap={4} direction="row" alignItems="center" justifyContent="space-between">

        <Stack direction="column" gap={1} flex={1}>

          <Typography variant="subtitle2" component="div">Order Date</Typography>

          <JSDatePicker
            sx={{
              flex: 1,
            }}
            value={purchaseOrder?.orderDate}
            onChange={(date) => handleOrderDateChange(date as Date)}
          />
        </Stack>


        <Stack direction="column" gap={1} flex={1}>
          <Typography variant="subtitle2" component="div">PO Number</Typography>
          <JSEditableInput
            outlined
            placeholder="PO Number"
            type="text"
            value={purchaseOrder?.poNumber || undefined}
            onValueChange={(value) => updatePurchaseOrder({ id: purchaseOrder?.id!, purchaseOrder: { poNumber: value } })}
          />
        </Stack>
      </Stack>


      {/* More Menu for Purchase Order */}
      <Menu
        anchorEl={orderMoreMenuAnchorEl}
        open={Boolean(orderMoreMenuAnchorEl)}
        onClose={handleOrderMoreMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >


        <MenuItem
          onClick={openPurchaseOrderEdit}
        >
          <ListItemIcon sx={{ color: 'text.primary' }}>
            <Icon icon="Edit01" size={IconSize.MEDIUM} />
          </ListItemIcon>
          <ListItemText>Edit Purchase Order</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleOrderMoreMenuClose();
            setDeleteOrderDialogOpen(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <Icon icon="Trash03" size={IconSize.MEDIUM} color="error" />
          </ListItemIcon>
          <ListItemText>Delete Purchase Order</ListItemText>
        </MenuItem>
      </Menu>


      <Box>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }} my={1} spacing={2}>
          <Typography variant="subtitle2" color="text.primary">Products ({purchaseOrder?.items.length})</Typography>

          <Typography variant="subtitle2" color="text.primary">
            Items ({purchaseOrder?.items.reduce((sum, item) => sum + (item.quantity || 0), 0)})
          </Typography>
        </Stack>

        {/* Items List */}

        <Stack gap={2}>

          {groupedItems?.map((group) => (

            <Stack direction="column" sx={{ border: 1, borderColor: APP_CONFIG.ui.theme.colors.border, borderRadius: 1, backgroundColor: APP_CONFIG.ui.theme.colors.background }}>

              <Stack direction="row" gap={2} p={2}>
                <ShipmentPreview
                  shipment={group.shipment!}
                  onClick={() => openEditShipment(group.shipment!)}
                />
              </Stack>

              <List disablePadding>
                {group?.items.map((item, index) => (
                  <ListItem key={`${item.product.id}-${index}`} divider={index !== group.items.length - 1}>

                    <Stack direction="column" gap={1} flex={1} pt={2}>

                      <ButtonBase
                        onClick={() => openProductDetailsDrawer(item.product)}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                          <ListItemAvatar>
                            <Avatar
                              src={item.product.image}
                              variant="rounded"
                              sx={{ width: 40, height: 40, border: 1, borderColor: APP_CONFIG.ui.theme.colors.border }}
                            />
                          </ListItemAvatar>

                          <Typography variant="subtitle2"
                            sx={{
                              textAlign: 'left',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'normal',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              display: '-webkit-box'
                            }}
                          >{item.product.title}</Typography>
                        </Stack>
                      </ButtonBase>

                      <Stack direction="row" flex={1} justifyContent="space-between" alignItems="center" spacing={2}>

                        <Typography variant="subtitle2" color="text.secondary">
                          EAN: {item.product.ean}
                        </Typography>

                        <Typography variant="subtitle2" color="text.secondary">
                          Production ready: {item.productionReadyDate ? format(new Date(item.productionReadyDate), 'MMM dd, yyyy') : 'N/A'}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Label
                            options={Object.values(PurchaseOrderItemStatus).map((status) => ({
                              value: status,
                              label: status,
                              color: PurchaseOrderItemStatusColors[status]
                            }))}
                            currentValue={item.status}
                            onOptionChanged={(value) => {
                              handleStatusChange(item.id!, value as PurchaseOrderItemStatus);
                            }}
                          />

                          <JSEditableInput
                            value={item.quantity}
                            suffix="items"
                            onValueChange={(value) => {
                              handleQuantityChange(item.id!, parseInt(value));
                            }}
                          />
                        </Stack>

                      </Stack>
                    </Stack>
                  </ListItem>

                ))}
              </List>
            </Stack>
          ))}

          {/* Ungrouped items section */}
          {ungroupedItems && ungroupedItems.length > 0 && (
            <Stack direction="column" sx={{ border: 1, borderColor: APP_CONFIG.ui.theme.colors.border, borderRadius: 1, backgroundColor: APP_CONFIG.ui.theme.colors.background }}>

              <List>
                {ungroupedItems.map((item, index) => (
                  <ListItem
                    divider={index !== ungroupedItems.length - 1}
                    key={`ungrouped-${item.product.id}-${index}`}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <ButtonBase
                      onClick={() => openProductDetailsDrawer(item.product)}
                    >
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                        <ListItemAvatar>
                          <Avatar
                            src={item.product.image}
                            variant="rounded"
                            sx={{ width: 40, height: 40, border: 1, borderColor: APP_CONFIG.ui.theme.colors.border }}
                          />
                        </ListItemAvatar>

                        <Typography variant="subtitle2"
                          sx={{
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            display: '-webkit-box'
                          }}
                        >{item.product.title}</Typography>
                      </Box>
                    </ButtonBase>

                    <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        EAN: {item.product.ean}
                      </Typography>

                      <Typography variant="subtitle2" color="text.secondary">
                        Production ready: {item.productionReadyDate ? format(new Date(item.productionReadyDate), 'MMM dd, yyyy') : 'N/A'}
                      </Typography>

                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Label
                          options={Object.values(PurchaseOrderItemStatus).map((status) => ({
                            value: status,
                            label: status,
                            color: PurchaseOrderItemStatusColors[status]
                          }))}
                          currentValue={item.status}
                          onOptionChanged={(value) => {
                            handleStatusChange(item.id!, value as PurchaseOrderItemStatus);
                          }}
                        />

                        <JSEditableInput
                          value={item.quantity}
                          suffix="items"
                          onValueChange={(value) => {
                            handleQuantityChange(item.id!, parseInt(value));
                          }}
                        />
                      </Stack>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}

          <Button
            sx={{ alignSelf: 'flex-start' }}
            variant="text"
            color="secondary"
            startIcon={<Add />}
            onClick={handleAddShipment}
          >
            Add Shipment
          </Button>


        </Stack>

      </Box>





      {/* <Box>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }} my={1} spacing={2}>
          <Typography variant="subtitle2" color="text.primary">Supplier</Typography>
        </Stack>


        <Box sx={{ border: 1, borderColor: APP_CONFIG.ui.theme.colors.border, borderRadius: 1, p: 2, backgroundColor: APP_CONFIG.ui.theme.colors.background }}>
          <Typography variant="subtitle2" color="text.secondary">
            {purchaseOrder?.supplier?.name || 'N/A'}
          </Typography>
        </Box>
      </Box> */}

      <Box >

        <Typography variant="subtitle2" sx={{ my: 1 }}>
          Order Details
        </Typography>

        <Box sx={{
          p: 2,
          border: 1,
          borderColor: APP_CONFIG.ui.theme.colors.border,
          backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
          borderRadius: 1
        }}>


          {/* Order Details */}
          <Grid container spacing={3}
          >
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Created</Typography>
              <Typography variant="body1">
                {purchaseOrder?.createdAt ? format(new Date(purchaseOrder.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">
                {purchaseOrder?.updatedAt ? format(new Date(purchaseOrder.updatedAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {
        purchaseOrder && (
          <PurchaseOrderNotes purchaseOrder={purchaseOrder} />
        )
      }

      <DeleteDialog
        open={deleteOrderDialogOpen}
        title="Delete Purchase Order"
        description="Are you sure you want to delete this purchase order? This action cannot be undone."
        cancelDelete={() => setDeleteOrderDialogOpen(false)}
        confirmDelete={handleDeletePurchaseOrder}
        isDeleting={isDeleting}
      />


    </Stack >
  );
};

export default PurchaseOrderDetails; 